import type {
  ExecuteCallback,
  ExecuteCallbackParams,
  SandboxExecutionResult,
} from "@shared/code/executor.ts";
import {
	createFetchAdapter,
	DSLM_HEADERS_INTERNAL_KEY,
	type EnrichedResponse,
	type FetchAdapterWithPreconnect,
	type SandboxEnvironmentOptions,
} from "@shared/code/quickjsSandbox.ts";
import { sandboxBundleSources } from "@shared/code/bundle/sandboxBundleSources.generated.ts";
import { acquirePureQuickJsModule } from "@shared/code/runtime/pureQuickjs.ts";
import {
  QuickJSHandle,
  Scope,
  type JSModuleLoaderAsync,
  type QuickJSAsyncContext,
} from "quickjs-emscripten-core";
import type { JSRuntimePointer } from "@jitl/quickjs-ffi-types";
import type {
  ConsoleLogEntry,
  ConsoleLogLevel,
  JsonValue,
} from "@shared/code/types.ts";

const DEFAULT_MEMORY_LIMIT_BYTES = 64 * 1024 * 1024;
const DEFAULT_STACK_SIZE = 512 * 1024; // 512KB - prevent stack overflow attacks
const DEFAULT_TIMEOUT_MS = 60_000; // 60 seconds - prevent infinite loops

const FALLBACK_DISPOSE_SYMBOL = Symbol.for("Symbol.dispose");

const createDisposable = (dispose: () => void): Disposable => {
  const disposable = {} as Disposable;
  (disposable as unknown as Record<symbol, () => void>)[Symbol.dispose] = dispose;
  (disposable as unknown as Record<symbol, () => void>)[FALLBACK_DISPOSE_SYMBOL] =
    dispose;
  return disposable;
};

const getNodeBuffer = () =>
  (
    globalThis as {
      Buffer?: {
        from: (
          value: string,
          encoding: string,
        ) => { toString: (encoding: string) => string };
      };
    }
  ).Buffer;

const textEncoder = new TextEncoder();

// Adapted from https://deno.land/std@0.224.0/encoding/base64.ts
const base64Alphabet =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/".split("");

const encodeBytesToBase64 = (bytes: Uint8Array): string => {
  let result = "";
  let index = 2;

  for (; index < bytes.length; index += 3) {
    result += base64Alphabet[bytes[index - 2]! >> 2];
    result +=
      base64Alphabet[
        ((bytes[index - 2]! & 0x03) << 4) | (bytes[index - 1]! >> 4)
      ];
    result +=
      base64Alphabet[((bytes[index - 1]! & 0x0f) << 2) | (bytes[index]! >> 6)];
    result += base64Alphabet[bytes[index]! & 0x3f];
  }

  if (index === bytes.length + 1) {
    result += base64Alphabet[bytes[index - 2]! >> 2];
    result += base64Alphabet[(bytes[index - 2]! & 0x03) << 4];
    result += "==";
  }

  if (index === bytes.length) {
    result += base64Alphabet[bytes[index - 2]! >> 2];
    result +=
      base64Alphabet[
        ((bytes[index - 2]! & 0x03) << 4) | (bytes[index - 1]! >> 4)
      ];
    result += base64Alphabet[(bytes[index - 1]! & 0x0f) << 2];
    result += "=";
  }

  return result;
};

const encodeBase64 = (value: string): string => {
  const BufferCtor = getNodeBuffer();

  if (BufferCtor) {
    return BufferCtor.from(value, "utf-8").toString("base64");
  }

  return encodeBytesToBase64(textEncoder.encode(value));
};

const LOG_ENTRY_ENCODER = new TextEncoder();
const MAX_CONSOLE_LOG_ENTRIES = 100;
const MAX_CONSOLE_LOG_BYTES = 32 * 1024;
const CONSOLE_METHODS: ConsoleLogLevel[] = ["log", "info", "warn", "error"];
const CONSOLE_INSTALL_SOURCE = `
(() => {
  const collect = globalThis.__dslmCollectConsoleLog;
  delete globalThis.__dslmCollectConsoleLog;
  if (typeof collect !== "function") {
    return;
  }

  const consoleObject = {};
  const levels = ${JSON.stringify(CONSOLE_METHODS)};

  for (const level of levels) {
    consoleObject[level] = (...args) => {
      try {
        collect({
          level,
          arguments: Array.from(args ?? []),
        });
      } catch (_error) {
        // Ignore logging errors inside the sandbox
      }
    };
  }

  globalThis.console = consoleObject;
})();
`;

const toJsonValue = (
  value: unknown,
  seen = new WeakSet<object>(),
): JsonValue => {
  if (value === null) {
    return null;
  }

  if (value === undefined) {
    return "[undefined]";
  }

  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : String(value);
  }

  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "bigint") {
    return value.toString();
  }

  if (typeof value === "symbol") {
    return value.description ?? value.toString();
  }

  if (typeof value === "function") {
    return `[Function ${value.name ?? "anonymous"}]`;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack ?? null,
    } satisfies JsonValue;
  }

  if (ArrayBuffer.isView(value)) {
    return `[${value.constructor.name} length=${value.byteLength}]`;
  }

  if (value instanceof ArrayBuffer) {
    return `[ArrayBuffer byteLength=${value.byteLength}]`;
  }

  if (Array.isArray(value)) {
    if (seen.has(value)) {
      return "[Circular]";
    }
    seen.add(value);
    const result = value.map((entry) => toJsonValue(entry, seen));
    seen.delete(value);
    return result;
  }

  if (value instanceof Set) {
    return Array.from(value).map((entry) => toJsonValue(entry, seen));
  }

  if (value instanceof Map) {
    if (seen.has(value)) {
      return "[Circular]";
    }
    seen.add(value);
    const mappedEntries: Record<string, JsonValue> = {};
    for (const [key, entry] of value.entries()) {
      mappedEntries[String(key)] = toJsonValue(entry, seen);
    }
    seen.delete(value);
    return mappedEntries;
  }

  if (value && typeof value === "object") {
    if (seen.has(value as object)) {
      return "[Circular]";
    }
    seen.add(value as object);
    const mappedEntries: Record<string, JsonValue> = {};
    for (const [key, entry] of Object.entries(
      value as Record<string, unknown>,
    )) {
      mappedEntries[key] = toJsonValue(entry, seen);
    }
    seen.delete(value as object);
    return mappedEntries;
  }

  return String(value);
};

type LogCollector = {
  entries: ConsoleLogEntry[];
  push: (entry: ConsoleLogEntry) => void;
};

const createLogCollector = (
  maxEntries = MAX_CONSOLE_LOG_ENTRIES,
  maxBytes = MAX_CONSOLE_LOG_BYTES,
): LogCollector => {
  const entries: ConsoleLogEntry[] = [];
  let usedBytes = 0;

  return {
    entries,
    push: (entry) => {
      if (entries.length >= maxEntries) {
        return;
      }

      const entryBytes = LOG_ENTRY_ENCODER.encode(
        JSON.stringify(entry),
      ).byteLength;

      if (usedBytes + entryBytes > maxBytes) {
        return;
      }

      entries.push(entry);
      usedBytes += entryBytes;
    },
  };
};

const createConsoleLogEntry = (
  level: ConsoleLogLevel,
  args: unknown[],
): ConsoleLogEntry => ({
  level,
  arguments: args.map((value) => toJsonValue(value)),
  timestamp: new Date().toISOString(),
});


let moduleCounter = 0;

const createModuleDataUrl = (source: string): string => {
  moduleCounter = (moduleCounter + 1) % Number.MAX_SAFE_INTEGER;
  return `data:text/javascript;base64,${encodeBase64(source)}#${moduleCounter}`;
};

const SANDBOX_BUNDLE_IMPORTS = Array.from(sandboxBundleSources.keys())
  .map((specifier) => `import "${specifier}";`)
  .join("\n");

const sandboxPolyfillsSource = `
${SANDBOX_BUNDLE_IMPORTS}
(() => {
  if (typeof globalThis.btoa !== "function") {
    globalThis.btoa = env?.btoa ?? (value => value);
    globalThis.atob = env?.atob ?? (value => value);
  }
})();
`;

export const buildSandboxRunnerSource = (
  moduleUrl: string,
  input: string,
  includePolyfills = true,
): string => `
const __dslmModuleUrl = ${JSON.stringify(moduleUrl)};
const __dslmInput = ${JSON.stringify(input)};

${includePolyfills ? sandboxPolyfillsSource : ""}

export default (async () => {
  const { default: __dslmHandler } = await import(__dslmModuleUrl);
  return await __dslmHandler(__dslmInput);
})();
`;

const isDataModule = (specifier: string) => specifier.startsWith("data:");
const isHttpModule = (specifier: string) =>
  specifier.startsWith("https://") || specifier.startsWith("http://");

const bundleSources = sandboxBundleSources;
const builtinModuleSources = new Map<string, string>();

const fetchRemoteModule = async (specifier: string): Promise<string> => {
  const response = await fetch(specifier, {
    headers: {
      ['User-Agent']:'QuickJS/2.0'
    }
  });
  if (!response.ok) {
    throw new Error(`Import '${specifier}' failed`);
  }

  return await response.text();
};

const createModuleLoader = (
  moduleUrl: string,
  moduleSource: string,
): JSModuleLoaderAsync => {
  const localModuleCache = new Map<string, string>([[moduleUrl, moduleSource]]);

  return async (specifier) => {
    if (localModuleCache.has(specifier)) {
      return localModuleCache.get(specifier)!;
    }

    if (bundleSources.has(specifier)) {
      return bundleSources.get(specifier)!;
    }

    if (builtinModuleSources.has(specifier)) {
      return builtinModuleSources.get(specifier)!;
    }

    if (isHttpModule(specifier)) {
      const value = await fetchRemoteModule(specifier);
      localModuleCache.set(specifier, value);
      return value;
    }

    return {
      error: new Error(`Module '${specifier}' not installed or available`),
    };
  };
};

const normalizeModuleSpecifier = (
  baseName: string,
  requestedName: string,
): string => {
  if (isDataModule(requestedName)) {
    return requestedName;
  }


  if (
    requestedName.startsWith("https:/") &&
    !requestedName.startsWith("https://")
  ) {
    return requestedName.replace("https:/", "https://");
  }

  if (
    requestedName.startsWith("http:/") &&
    !requestedName.startsWith("http://")
  ) {
    return requestedName.replace("http:/", "http://");
  }

  if (isHttpModule(requestedName)) {
    return requestedName;
  }

  if (requestedName.startsWith("//")) {
    return `https:${requestedName}`;
  }

  if (requestedName.startsWith("/")) {
    if (baseName.startsWith("http")) {
      const base = new URL(baseName);
      return `${base.origin}${requestedName}`;
    }
    return requestedName;
  }

  if (requestedName.startsWith(".")) {
    if (baseName.startsWith("http")) {
      return new URL(requestedName, baseName).toString();
    }
    return requestedName;
  }

  return requestedName;
};

const moduleNormalizer = (baseName: string, requestedName: string): string =>
  normalizeModuleSpecifier(baseName, requestedName);

const normalizeQuickJsError = (value: unknown): Error => {
  if (value instanceof Error) {
    return value;
  }
  if (value && typeof value === "object") {
    const maybeName = (value as { name?: unknown }).name;
    const maybeMessage = (value as { message?: unknown }).message;
    const err = new Error(
      typeof maybeMessage === "string"
        ? maybeMessage
        : JSON.stringify(value, null, 2),
    );
    err.name = typeof maybeName === "string" ? maybeName : "Error";
    const stack = (value as { stack?: unknown }).stack;
    if (typeof stack === "string") {
      err.stack = stack;
    }
    return err;
  }
  return new Error(String(value));
};

const hostBtoa = (value: string): string => {
  if (typeof globalThis.btoa === "function") {
    return globalThis.btoa(value);
  }

  const BufferCtor = getNodeBuffer();
  return BufferCtor
    ? BufferCtor.from(value, "utf-8").toString("base64")
    : encodeBytesToBase64(textEncoder.encode(value));
};

const hostAtob = (value: string): string => {
  if (typeof globalThis.atob === "function") {
    return globalThis.atob(value);
  }

  const BufferCtor = getNodeBuffer();
  return BufferCtor
    ? BufferCtor.from(value, "base64").toString("utf-8")
    : value;
};

const installEnv = (context: QuickJSAsyncContext) => {
  const envHandle = context.newObject();

  const btoaHandle = context.newFunction("btoa", (_this, input) => {
    const raw = context.dump(input);
    const text = typeof raw === "string" ? raw : String(raw ?? "");
    const encoded = hostBtoa(text);
    return context.newString(encoded);
  });

  const atobHandle = context.newFunction("atob", (_this, input) => {
    const raw = context.dump(input);
    const text = typeof raw === "string" ? raw : String(raw ?? "");
    const decoded = hostAtob(text);
    return context.newString(decoded);
  });

  context.setProp(envHandle, "btoa", btoaHandle);
  context.setProp(envHandle, "atob", atobHandle);
  context.setProp(context.global, "env", envHandle);

  btoaHandle.dispose();
  atobHandle.dispose();
  envHandle.dispose();
};

const installTextEncoding = (context: QuickJSAsyncContext) => {
  context.unwrapResult(
    context.evalCode(`
      if (typeof globalThis.TextEncoder !== "function") {
        globalThis.TextEncoder = class TextEncoder {
          encode(input = "") {
            const str = String(input);
            const bytes = [];
            for (let i = 0; i < str.length; i++) {
              let codePoint = str.codePointAt(i);
              if (codePoint === undefined) continue;
              if (codePoint > 0xffff) {
                i++;
              }
              if (codePoint <= 0x7f) {
                bytes.push(codePoint);
              } else if (codePoint <= 0x7ff) {
                bytes.push(
                  0xc0 | (codePoint >> 6),
                  0x80 | (codePoint & 0x3f),
                );
              } else if (codePoint <= 0xffff) {
                bytes.push(
                  0xe0 | (codePoint >> 12),
                  0x80 | ((codePoint >> 6) & 0x3f),
                  0x80 | (codePoint & 0x3f),
                );
              } else {
                bytes.push(
                  0xf0 | (codePoint >> 18),
                  0x80 | ((codePoint >> 12) & 0x3f),
                  0x80 | ((codePoint >> 6) & 0x3f),
                  0x80 | (codePoint & 0x3f),
                );
              }
            }
            return Uint8Array.from(bytes);
          }
        };
      }

      if (typeof globalThis.TextDecoder !== "function") {
        globalThis.TextDecoder = class TextDecoder {
          decode(input = new Uint8Array()) {
            const bytes = input instanceof Uint8Array ? input : new Uint8Array(input ?? []);
            let result = "";
            for (let i = 0; i < bytes.length;) {
              const byte1 = bytes[i++];
              if (byte1 < 0x80) {
                result += String.fromCharCode(byte1);
                continue;
              }
              let codePoint = 0;
              let extraBytes = 0;
              if (byte1 >= 0xf0) {
                codePoint = byte1 & 0x07;
                extraBytes = 3;
              } else if (byte1 >= 0xe0) {
                codePoint = byte1 & 0x0f;
                extraBytes = 2;
              } else {
                codePoint = byte1 & 0x1f;
                extraBytes = 1;
              }
              for (let j = 0; j < extraBytes; j++) {
                const nextByte = bytes[i++];
                codePoint = (codePoint << 6) | (nextByte & 0x3f);
              }
              if (codePoint <= 0xffff) {
                result += String.fromCharCode(codePoint);
              } else {
                codePoint -= 0x10000;
                result += String.fromCharCode(
                  0xd800 + ((codePoint >> 10) & 0x3ff),
                  0xdc00 + (codePoint & 0x3ff),
                );
              }
            }
            return result;
          }
        };
      }
    `),
  );
};

const isConsoleLogLevel = (value: unknown): value is ConsoleLogLevel =>
  typeof value === "string" &&
  CONSOLE_METHODS.includes(value as ConsoleLogLevel);

const installConsole = (
  context: QuickJSAsyncContext,
  logCollector?: (entry: ConsoleLogEntry) => void,
) => {
  const sinkHandle = context.newFunction(
    "__dslmCollectConsoleLog",
    (payloadHandle) => {
      if (!payloadHandle) {
        return context.undefined.dup();
      }

      const payload = context.dump(payloadHandle) as
        | {
            level?: unknown;
            arguments?: unknown;
          }
        | undefined;

      const level = isConsoleLogLevel(payload?.level)
        ? payload?.level
        : "log";
      const argumentCandidates = payload?.arguments;
      const rawArguments = Array.isArray(argumentCandidates)
        ? [...argumentCandidates]
        : [];

      const entry = createConsoleLogEntry(level, rawArguments);

      if (logCollector) {
        try {
          logCollector(entry);
        } catch {
          // Ignore collector failures
        }
      }

      const consoleMethod = console[level] ?? console.log;
      try {
        if (typeof consoleMethod === "function") {
          Reflect.apply(
            consoleMethod as (...args: unknown[]) => unknown,
            console,
            rawArguments,
          );
        } else {
          console.log(...rawArguments);
        }
      } catch {
        // Swallow host console errors to avoid breaking sandbox execution
      }

      return context.undefined.dup();
    },
  );

  context.setProp(context.global, "__dslmCollectConsoleLog", sinkHandle);
  sinkHandle.dispose();

  context.unwrapResult(context.evalCode(CONSOLE_INSTALL_SOURCE));
};

const valueToHandle = (
  context: QuickJSAsyncContext,
  value: unknown,
  seen = new WeakMap<object, QuickJSHandle>(),
): QuickJSHandle => {
  if (value === undefined) {
    return context.undefined.dup();
  }

  if (value === null) {
    return context.null.dup();
  }

  if (typeof value === "string") {
    return context.newString(value);
  }

  if (typeof value === "number") {
    return context.newNumber(value);
  }

  if (typeof value === "boolean") {
    return value ? context.true.dup() : context.false.dup();
  }

  if (typeof value === "bigint") {
    return context.newBigInt(value);
  }

  if (Array.isArray(value)) {
    const arrayHandle = context.newArray();
    value.forEach((entry, index) => {
      const entryHandle = valueToHandle(context, entry, seen);
      context.setProp(arrayHandle, index, entryHandle);
      entryHandle.dispose();
    });
    return arrayHandle;
  }

  if (value instanceof ArrayBuffer) {
    return context.newArrayBuffer(value.slice(0));
  }

  if (ArrayBuffer.isView(value)) {
    return context.newArrayBuffer(value.buffer.slice(0));
  }

  if (value && typeof value === "object") {
    if (seen.has(value as object)) {
      return seen.get(value as object)!.dup();
    }

    const objectHandle = context.newObject();
    seen.set(value as object, objectHandle);

    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      const childHandle = valueToHandle(context, val, seen);
      context.setProp(objectHandle, key, childHandle);
      childHandle.dispose();
    }

    return objectHandle;
  }

  return context.undefined.dup();
};

const createHeadersHandle = (
  context: QuickJSAsyncContext,
  headers: Headers,
) => {
  const headersHandle = context.newObject();
  const headerMap = new Map<string, string>();

  headers.forEach((value, key) => {
    headerMap.set(key.toLowerCase(), value);
  });

  const getHandle = context.newFunction("get", (_this, keyHandle) => {
    const raw = context.dump(keyHandle);
    const key =
      typeof raw === "string"
        ? raw.toLowerCase()
        : String(raw ?? "").toLowerCase();
    const match = headerMap.get(key);
    return typeof match === "string"
      ? context.newString(match)
      : context.undefined.dup();
  });

  context.setProp(headersHandle, "get", getHandle);
  getHandle.dispose();

  for (const [key, value] of headerMap.entries()) {
    const valueHandle = context.newString(value);
    context.setProp(headersHandle, key, valueHandle);
    valueHandle.dispose();
  }

  return headersHandle;
};

const createResponseHandle = (
  context: QuickJSAsyncContext,
  response: EnrichedResponse,
) => {
  const responseHandle = context.newObject();

  const setProp = (name: string, handle: QuickJSHandle) => {
    context.setProp(responseHandle, name, handle);
    handle.dispose();
  };

  setProp("status", context.newNumber(response.status));
  setProp("ok", response.ok ? context.true.dup() : context.false.dup());
  setProp("statusText", context.newString(response.statusText));
  setProp("url", context.newString(response.url ?? ""));
  setProp(
    "redirected",
    response.redirected ? context.true.dup() : context.false.dup(),
  );

  const headersHandle = createHeadersHandle(context, response.headers);
  context.setProp(responseHandle, "headers", headersHandle);
  headersHandle.dispose();

  const textFn = context.newFunction("text", () => {
    const text = response.__bodyText ?? "";
    return context.newString(text);
  });

  context.setProp(responseHandle, "text", textFn);
  textFn.dispose();

  const jsonFn = context.newFunction("json", () => {
    const payload = response.__bodyJson ?? null;
    return valueToHandle(context, payload);
  });

  context.setProp(responseHandle, "json", jsonFn);
  jsonFn.dispose();

  return responseHandle;
};

const installFetch = (
  context: QuickJSAsyncContext,
  adapter: FetchAdapterWithPreconnect,
) => {
  const fetchHandle = context.newFunction(
    "fetch",
    (inputHandle, initHandle?: QuickJSHandle) => {
      const input = context.dump(inputHandle) as RequestInfo | URL;
      const init =
        typeof initHandle === "undefined"
          ? undefined
          : (context.dump(initHandle) as RequestInit);
      const deferred = context.newPromise();

      adapter(input, init)
        .then((response) => {
          const handle = createResponseHandle(context, response);
          deferred.resolve(handle);
          handle.dispose();
        })
        .catch((error) => {
          const message =
            error instanceof Error ? error.message : String(error ?? "");
          const errHandle = context.newError(message);
          deferred.reject(errHandle);
          errHandle.dispose();
        });

      return deferred.handle;
    },
  );

  context.setProp(context.global, "fetch", fetchHandle);
  fetchHandle.dispose();
};

const installTimers = (context: QuickJSAsyncContext) => {
  const hostSetTimeout = globalThis.setTimeout?.bind(globalThis);
  const hostClearTimeout = globalThis.clearTimeout?.bind(globalThis);

  if (
    typeof hostSetTimeout !== "function" ||
    typeof hostClearTimeout !== "function"
  ) {
    return () => {};
  }

  type TimerRecord = { cancel: () => void };
  let nextTimerId = 1;
  const activeTimers = new Map<number, TimerRecord>();
  let disposed = false;

  const disposeHandles = (callback: QuickJSHandle, args: QuickJSHandle[]) => {
    callback.dispose();
    for (const handle of args) {
      handle.dispose();
    }
  };

  const cleanup = () => {
    if (disposed) return;
    disposed = true;
    for (const [, record] of activeTimers) {
      try {
        record.cancel();
      } catch (error) {
        console.error("[sandbox] Failed to cancel timer during cleanup", error);
      }
    }
    activeTimers.clear();
  };

  const setTimeoutHandle = context.newFunction(
    "setTimeout",
    (
      callbackHandle?: QuickJSHandle,
      delayHandle?: QuickJSHandle,
      ...restHandles: QuickJSHandle[]
    ) => {
      if (!callbackHandle) {
        return { error: context.newError("setTimeout requires a callback") };
      }

      if (context.typeof(callbackHandle) !== "function") {
        return {
          error: context.newError("setTimeout callback must be a function"),
        };
      }

      const callback = callbackHandle.dup();
      const args = restHandles.map((handle) => handle.dup());
      const parsedDelay =
        typeof delayHandle === "undefined"
          ? 0
          : Number(context.dump(delayHandle));
      const delay = Number.isFinite(parsedDelay) && parsedDelay > 0
        ? parsedDelay
        : 0;

      if (disposed) {
        disposeHandles(callback, args);
        return context.newNumber(0);
      }

      const timerId = nextTimerId++;
      const timeoutRef = hostSetTimeout(() => {
        activeTimers.delete(timerId);
        if (disposed) {
          disposeHandles(callback, args);
          return;
        }
        try {
          const result = context.callFunction(
            callback,
            context.undefined,
            ...args,
          );
          const valueHandle = context.unwrapResult(result);
          valueHandle.dispose();
        } catch (error) {
          console.error("[sandbox] setTimeout callback failed", error);
        } finally {
          disposeHandles(callback, args);
        }
      }, delay);

      activeTimers.set(timerId, {
        cancel: () => {
          hostClearTimeout(timeoutRef);
          disposeHandles(callback, args);
        },
      });

      return context.newNumber(timerId);
    },
  );

  context.setProp(context.global, "setTimeout", setTimeoutHandle);
  setTimeoutHandle.dispose();

  const clearTimeoutHandle = context.newFunction(
    "clearTimeout",
    (idHandle?: QuickJSHandle) => {
      if (disposed) {
        return context.undefined.dup();
      }

      if (typeof idHandle === "undefined") {
        return context.undefined.dup();
      }

      const numericId = Number(context.dump(idHandle));
      if (Number.isFinite(numericId)) {
        const timerId = Math.trunc(numericId);
        const record = activeTimers.get(timerId);
        if (record) {
          activeTimers.delete(timerId);
          record.cancel();
        }
      }

      return context.undefined.dup();
    },
  );

  context.setProp(context.global, "clearTimeout", clearTimeoutHandle);
  clearTimeoutHandle.dispose();

  return cleanup;
};

const installBaseGlobals = (context: QuickJSAsyncContext) => {
  context.unwrapResult(
    context.evalCode(
      `
      if (typeof globalThis.global === "undefined") {
        globalThis.global = globalThis;
      }
      if (typeof globalThis.self === "undefined") {
        globalThis.self = globalThis;
      }
      if (typeof globalThis.window === "undefined") {
        globalThis.window = globalThis;
      }
    `,
    ),
  );
};

const installRequestPolyfills = (context: QuickJSAsyncContext) => {
  const headersKey = JSON.stringify(DSLM_HEADERS_INTERNAL_KEY);
  context.unwrapResult(
    context.evalCode(`
      (() => {
        const INTERNAL_HEADERS_KEY = ${headersKey};

        if (typeof globalThis.Headers !== "function") {
          class HeadersPolyfill {
            constructor(init = {}) {
              this[INTERNAL_HEADERS_KEY] = Object.create(null);
              this._populate(init);
            }

            _normalizeKey(name) {
              return String(name ?? "").toLowerCase();
            }

            _normalizeValue(value) {
              return String(value ?? "");
            }

            _populate(init) {
              if (!init) return;
              if (init instanceof HeadersPolyfill) {
                init.forEach((value, key) => this.set(key, value));
                return;
              }
              if (Array.isArray(init)) {
                init.forEach(([key, value]) => this.append(key, value));
                return;
              }
              if (typeof init === "object") {
                Object.keys(init).forEach((key) => this.set(key, init[key]));
              }
            }

            append(name, value) {
              const key = this._normalizeKey(name);
              const normalized = this._normalizeValue(value);
              if (Object.prototype.hasOwnProperty.call(this[INTERNAL_HEADERS_KEY], key)) {
                this[INTERNAL_HEADERS_KEY][key] += ", " + normalized;
              } else {
                this[INTERNAL_HEADERS_KEY][key] = normalized;
              }
            }

            set(name, value) {
              const key = this._normalizeKey(name);
              this[INTERNAL_HEADERS_KEY][key] = this._normalizeValue(value);
            }

            get(name) {
              const key = this._normalizeKey(name);
              return Object.prototype.hasOwnProperty.call(this[INTERNAL_HEADERS_KEY], key)
                ? this[INTERNAL_HEADERS_KEY][key]
                : null;
            }

            has(name) {
              const key = this._normalizeKey(name);
              return Object.prototype.hasOwnProperty.call(this[INTERNAL_HEADERS_KEY], key);
            }

            delete(name) {
              const key = this._normalizeKey(name);
              delete this[INTERNAL_HEADERS_KEY][key];
            }

            forEach(callback, thisArg) {
              for (const key of Object.keys(this[INTERNAL_HEADERS_KEY])) {
                callback.call(thisArg, this[INTERNAL_HEADERS_KEY][key], key, this);
              }
            }

            entries() {
              return Object.entries(this[INTERNAL_HEADERS_KEY])[Symbol.iterator]();
            }

            keys() {
              return Object.keys(this[INTERNAL_HEADERS_KEY])[Symbol.iterator]();
            }

            values() {
              return Object.values(this[INTERNAL_HEADERS_KEY])[Symbol.iterator]();
            }

            [Symbol.iterator]() {
              return this.entries();
            }

            clone() {
              return new HeadersPolyfill(this[INTERNAL_HEADERS_KEY]);
            }

            toJSON() {
              const copy = {};
              this.forEach((value, key) => {
                copy[key] = value;
              });
              return copy;
            }
          }

          globalThis.Headers = HeadersPolyfill;
        }

        if (typeof globalThis.Request !== "function") {
          class RequestPolyfill {
            constructor(input, init = {}) {
              if (!(this instanceof RequestPolyfill)) {
                throw new TypeError("Class constructor Request cannot be invoked without 'new'");
              }

              const base = this._normalizeInput(input);
              const mergedInit = {
                method: init.method ?? base.method ?? "GET",
                headers: init.headers ?? base.headers ?? {},
                body: init.body ?? base.body ?? null,
                redirect: init.redirect ?? base.redirect ?? "follow",
                signal: init.signal ?? base.signal ?? null,
                credentials: init.credentials ?? base.credentials ?? "same-origin",
                cache: init.cache ?? base.cache ?? "default",
                mode: init.mode ?? base.mode ?? "cors",
              };

              this.url = base.url;
              this.method = String(mergedInit.method ?? "GET").toUpperCase();
              const headerInit = mergedInit.headers;
              this.headers = headerInit instanceof Headers
                ? headerInit.clone()
                : new Headers(headerInit);
              this.body = mergedInit.body;
              this.redirect = mergedInit.redirect;
              this.signal = mergedInit.signal;
              this.credentials = mergedInit.credentials;
              this.cache = mergedInit.cache;
              this.mode = mergedInit.mode;
            }

            _normalizeInput(input) {
              if (input instanceof RequestPolyfill) {
                return {
                  url: input.url,
                  method: input.method,
                  headers: input.headers.clone(),
                  body: input.body,
                  redirect: input.redirect,
                  signal: input.signal,
                  credentials: input.credentials,
                  cache: input.cache,
                  mode: input.mode,
                };
              }

              if (typeof input === "string") {
                return { url: input };
              }

              if (input && typeof input === "object") {
                const candidateUrl =
                  typeof input.url === "string"
                    ? input.url
                    : typeof input.href === "string"
                      ? input.href
                      : String(input);

                return {
                  url: candidateUrl,
                  method: input.method,
                  headers: input.headers,
                  body: input.body,
                  redirect: input.redirect,
                  signal: input.signal,
                  credentials: input.credentials,
                  cache: input.cache,
                  mode: input.mode,
                };
              }

              return { url: String(input ?? "") };
            }

            clone() {
              return new RequestPolyfill(this);
            }
          }

          globalThis.Request = RequestPolyfill;
        }
      })();
    `),
  );
};

const prepareContext = (
  context: QuickJSAsyncContext,
  fetchAdapter: FetchAdapterWithPreconnect,
  logCollector?: (entry: ConsoleLogEntry) => void,
) => {
  const startTime = Date.now();

  // Set resource limits to prevent DoS attacks
  context.runtime.setMemoryLimit(DEFAULT_MEMORY_LIMIT_BYTES);
  context.runtime.setMaxStackSize(DEFAULT_STACK_SIZE);

  // Set interrupt handler to prevent infinite loops
  context.runtime.setInterruptHandler(() => {
    const elapsed = Date.now() - startTime;
    return elapsed > DEFAULT_TIMEOUT_MS;
  });

  const cleanupFns: Array<() => void> = [];

  installBaseGlobals(context);
  installEnv(context);
  installConsole(context, logCollector);
  installTextEncoding(context);
  installRequestPolyfills(context);
  installFetch(context, fetchAdapter);
  cleanupFns.push(installTimers(context));

  return () => {
    for (const cleanupFn of cleanupFns) {
      try {
        cleanupFn();
      } catch (error) {
        console.error("[sandbox] Failed to clean up sandbox environment", error);
      }
    }
  };
};

type QuickJSRuntimeInternals = {
  ffi: {
    QTS_ExecutePendingJob: (
      rt: number,
      maxJobs: number,
      ctxOutPtr: number,
    ) => number;
    QTS_ExecutePendingJob_MaybeAsync?: (
      rt: number,
      maxJobs: number,
      ctxOutPtr: number,
    ) => Promise<number>;
    QTS_FreeValuePointerRuntime: (rt: number, ptr: number) => void;
  };
  memory: {
    newMutablePointerArray: (size: number) => {
      value: { ptr: number; typedArray: Int32Array };
      dispose: () => void;
    };
  };
  rt: { value: number };
  contextMap: Map<number, QuickJSAsyncContext>;
  newContext: (options: { contextPointer: number }) => QuickJSAsyncContext;
};

const runPendingJobs = async (context: QuickJSAsyncContext) => {
  const runtime = context.runtime as unknown as QuickJSRuntimeInternals;
  const ffi = runtime.ffi as QuickJSRuntimeInternals["ffi"];
  const ctxPtrOut = runtime.memory.newMutablePointerArray(1);
  const jsRuntimePtr = runtime.rt.value as unknown as JSRuntimePointer;

  try {
    const syncExecute = ffi.QTS_ExecutePendingJob as unknown as (
      rt: number,
      maxJobs: number,
      ctxOutPtr: number,
    ) => number;
    const asyncExecute = ffi.QTS_ExecutePendingJob_MaybeAsync
      ? (ffi.QTS_ExecutePendingJob_MaybeAsync as unknown as (
          rt: number,
          maxJobs: number,
          ctxOutPtr: number,
        ) => Promise<number>)
      : null;
    const valuePtr = asyncExecute
      ? await asyncExecute(jsRuntimePtr, -1, ctxPtrOut.value.ptr)
      : syncExecute(jsRuntimePtr, -1, ctxPtrOut.value.ptr);
    const ctxPtr = ctxPtrOut.value.typedArray[0];

    if (ctxPtr === 0) {
      const freePtr = ffi.QTS_FreeValuePointerRuntime as unknown as (
        rt: JSRuntimePointer,
        ptr: number,
      ) => void;
      freePtr(jsRuntimePtr, valuePtr);
      return;
    }

    const contextMap = runtime.contextMap;
    const targetContext =
      contextMap.get(ctxPtr) ?? runtime.newContext({ contextPointer: ctxPtr });
    const targetMemory = targetContext.getMemory(jsRuntimePtr) as unknown as {
      heapValueHandle: (ptr: number) => QuickJSHandle;
    };
    const resultHandle = targetMemory.heapValueHandle(valuePtr);
    const resultType = targetContext.typeof(resultHandle);

    if (resultType === "number") {
      resultHandle.dispose();
      return;
    }

    const errorValue = targetContext.dump(resultHandle);
    resultHandle.dispose();
    throw normalizeQuickJsError(errorValue);
  } finally {
    ctxPtrOut.dispose();
  }
};

const resolveHandle = async (
  context: QuickJSAsyncContext,
  initialHandle: QuickJSHandle,
): Promise<QuickJSHandle> => {
  const yieldToHost = () =>
    new Promise<void>((resolve) => {
      setTimeout(resolve, 0);
    });
  let handle = initialHandle;

  while (true) {
    const state = context.getPromiseState(handle);

    if (state.type === "pending") {
      await runPendingJobs(context);
      await yieldToHost();
      continue;
    }

    if (state.type === "fulfilled" && state.notAPromise) {
      return handle;
    }

    if (state.type === "fulfilled" && state.value) {
      handle.dispose();
      handle = state.value;
      continue;
    }

    if (state.type === "rejected") {
      const errorValue = context.dump(state.error);
      state.error.dispose();
      handle.dispose();
      throw normalizeQuickJsError(errorValue);
    }

    return handle;
  }
};

export const createSandboxExecute = (
  environmentOverrides?: SandboxEnvironmentOptions,
): ExecuteCallback => {
  const fetchAdapter =
    environmentOverrides?.fetchAdapter ?? createFetchAdapter();

  return async ({ moduleSource, input }: ExecuteCallbackParams) => {
    using moduleLease = await acquirePureQuickJsModule();
    const module = moduleLease.module;
    const scope = new Scope();
    using _scope = createDisposable(() => scope.dispose());
    const context = scope.manage(module.newContext());
    const logCollector = createLogCollector();

    try {
      const cleanupEnvironment = prepareContext(
        context,
        fetchAdapter,
        (entry) => logCollector.push(entry),
      );
      using _cleanup = createDisposable(cleanupEnvironment);

      const moduleUrl = createModuleDataUrl(moduleSource);
      const runnerSource = buildSandboxRunnerSource(moduleUrl, input, true);
      const runnerModuleUrl = createModuleDataUrl(runnerSource);

      const loader = createModuleLoader(moduleUrl, moduleSource);
      context.runtime.setModuleLoader(loader, moduleNormalizer);

      const evalResult = await context.evalCodeAsync(
        runnerSource,
        runnerModuleUrl,
        {
          type: "module",
          strict: true,
          backtraceBarrier: true,
        },
      );
      const moduleHandle = scope.manage(context.unwrapResult(evalResult));
      const defaultExportHandle = context.getProp(moduleHandle, "default");
      const resolvedHandle = await resolveHandle(context, defaultExportHandle);

      const data = context.dump(resolvedHandle);
      resolvedHandle.dispose();

      return {
        ok: true,
        data,
        logs: logCollector.entries.slice(),
      } satisfies SandboxExecutionResult;
    } catch (error) {
      return {
        ok: false,
        error,
        logs: logCollector.entries.slice(),
      } satisfies SandboxExecutionResult;
    }
  };
};

export const createUnsafeExecute = (): ExecuteCallback => {
  return async ({ moduleSource, input }: ExecuteCallbackParams) => {
    try {
      const moduleUrl = createModuleDataUrl(moduleSource);
      const runnerSource = buildSandboxRunnerSource(
        moduleUrl,
        input,
        false /* includePolyfills */,
      );
      const runnerUrl = createModuleDataUrl(runnerSource);
      const module = await import(/* @vite-ignore */ runnerUrl);
      const data = await module?.default;
      return {
        ok: true,
        data,
        logs: [],
      } satisfies SandboxExecutionResult;
    } catch (error) {
      return {
        ok: false,
        error,
        logs: [],
      } satisfies SandboxExecutionResult;
    }
  };
};
